import styled, { css, keyframes } from 'styled-components';
import { Box, Text } from '@rsos/components/capstone/base';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import { QUEUE_FULL_WIDTH } from '@rsos/constants/queueWidths';
import getCallStatusColor from '@rsos/utils/getCallStatusColor';

export const blink = keyframes`
  50% {
    opacity: 20%;
  }
`;

const tf911NotificationDot = css`
  content: '';
  position: absolute;
  top: 50%;
  left: 8px;
  width: 8px;
  height: 8px;
  margin-top: -4px; /* Center the 8px dot vertically */
  background-color: ${({ theme }) => theme.colors.primaryBase}; // Blue
  border-radius: 50%;
  animation: ${blink} 1s ease-in-out infinite;
`;

const rcsNotificationDot = css`
  content: '';
  position: absolute;
  top: 50%;
  left: 8px;
  width: 8px;
  height: 8px;
  margin-top: -4px; /* Center the 8px dot vertically */
  background-color: ${({ theme }) => theme.colors.success}; // Green
  border-radius: 50%;
  animation: ${blink} 1s ease-in-out infinite;
`;

const rcsOriginalBar = css`
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 8px;
  width: 4px;
  background-color: ${({ theme }) => theme.colors.success}; // Green
  border-radius: 2px;
  animation: ${blink} 1s ease-in-out infinite;
`;

const regularNotificationDot = css`
  content: '';
  position: absolute;
  top: 50%;
  left: 8px;
  width: 8px;
  height: 8px;
  margin-top: -4px; /* Center the 8px dot vertically */
  background-color: ${({ theme }) => theme.colors.primaryBase}; // Blue
  border-radius: 50%;
  animation: ${blink} 1s ease-in-out infinite;
`;

const notificationBar = css`
  content: '';
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 7px;
  width: 3px;
  height: calc(100% - 10px);
  margin: 5px auto;
  background-color: ${({ theme }) => theme.colors.primaryBase};
  border-radius: 6px;
  animation: ${blink} 1s ease-in-out infinite;
`;

const rcsNotificationBar = css`
  content: '';
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 7px;
  width: 3px;
  height: calc(100% - 10px);
  margin: 5px auto;
  background-color: ${({ theme }) => theme.colors.success};
  border-radius: 6px;
  animation: ${blink} 1s ease-in-out infinite;
`;

const emergencyCallBg = (
  isHovered,
  isSelected,
  unreadTF911Message,
  unreadRcsMessage,
  isUnread,
  isUnseen,
) => {
  let backgroundColor = 'background';

  const shouldHighlightForNewMessages = unreadTF911Message || unreadRcsMessage;

  if (shouldHighlightForNewMessages || isUnread || isUnseen) {
    backgroundColor = 'tabHighlight';
  } else if (isHovered) {
    backgroundColor = 'foreground';
  } else if (isSelected) {
    backgroundColor = 'highlights';
  }

  return backgroundColor;
};

export const Container = styled(Box).attrs(
  ({
    isExpired,
    isHovered,
    isSelected,
    unreadTF911Message,
    unreadRcsMessage,
    isUnread,
    isUnseen,
  }) => ({
    horizontal: true,
    align: 'center',
    justify: 'space-between',
    position: 'relative',
    width: '100%',
    minHeight: 70,
    backgroundColor: emergencyCallBg(
      isHovered,
      isSelected,
      unreadTF911Message,
      unreadRcsMessage,
      isUnread,
      isUnseen,
    ),
    padding: '8px 24px 8px 22px',
    cursor: 'pointer',
    opacity: isExpired ? 0.4 : 1,
  }),
)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightLineDivider};
  font-family: 'Roboto', sans-serif;
  user-select: none;

  /* Diagonal stripes for collaborator */
  ${({ isCollaborator }) =>
    isCollaborator &&
    `background-image: 
      repeating-linear-gradient(
        -45deg, 
        transparent, 
        transparent 8px, 
        rgba(128, 128, 128, 0.2) 8px,
        rgba(128, 128, 128, 0.2) 9px
      );
    `}
  &:hover {
    background-color: ${({
      theme,
      isSelected,
      unreadTF911Message,
      unreadRcsMessage,
      isUnread,
      isUnseen,
    }) =>
      !isSelected &&
      !unreadTF911Message &&
      !unreadRcsMessage &&
      !isUnread &&
      !isUnseen &&
      theme.colors.foreground};
  }
  &:before {
    /* TF911 - only show dot if user has access to new indicators */
    ${({ unreadTF911Message }) => unreadTF911Message && tf911NotificationDot};

    /* RCS - show dot if has access, show bar if no access */
    ${({ unreadRcsMessage, unreadTF911Message }) =>
      !unreadTF911Message && unreadRcsMessage && rcsNotificationDot};

    /* regular unread - keep original dot behavior */
    ${({ isUnread, isUnseen, unreadTF911Message, unreadRcsMessage }) =>
      !unreadTF911Message &&
      !unreadRcsMessage &&
      (isUnread || isUnseen) &&
      regularNotificationDot};
  }
  @media (max-width: ${BP_LAPTOP}px) {
    width: ${QUEUE_FULL_WIDTH};
  }
`;

export const IconBox = styled(Box).attrs({
  flex: '0 0 auto',
  marginRight: 22,
  color: 'secondaryText',
})``;

export const CallBox = styled(Box).attrs({
  flex: '1 1 auto',
  overflow: 'hidden',
})``;

export const PhoneNumber = styled(Text).attrs(
  ({ isUnseen, unreadTF911Message, unreadRcsMessage, isUnread }) => ({
    color:
      unreadTF911Message || unreadRcsMessage || isUnread
        ? 'primaryText'
        : isUnseen
          ? 'queue.emergencyCall.unseen'
          : 'primaryText',
    size: 'large',
    fontWeight: 'bold', // Always bold
  }),
)``;

export const PhoneDetails = styled(Text).attrs({
  marginTop: '4px',
  color: 'secondaryText',
  size: 'small',
  textTransform: 'uppercase',
})``;

export const TF911NewMessage = styled(Text).attrs({
  marginTop: '4px',
  color: 'primaryText',
  size: 'small',
  fontWeight: 'bold',
  textTransform: 'uppercase',
})``;

export const RcsNewMessage = styled(Text).attrs({
  marginTop: '4px',
  color: 'primaryText',
  size: 'small',
  fontWeight: 'bold',
  textTransform: 'uppercase',
})``;

export const RegularNewMessage = styled(Text).attrs({
  marginTop: '4px',
  color: 'primaryText',
  size: 'small',
  fontWeight: 'bold',
  textTransform: 'uppercase',
})``;

// Due to contract with SiriusXM, the text color needs to be 'heavyWarning'.
export const SiriusXMMessage = styled(PhoneDetails).attrs({
  color: 'heavyWarning',
})``;

export const CollaboratorMessage = styled(PhoneDetails).attrs({
  color: 'heavyWarning',
})``;

export const ConnectedSiteMessage = styled(PhoneDetails).attrs({
  color: 'heavyWarning',
})``;

export const CallStatusTag = styled(Box).attrs(({ status, isCallPopup }) => ({
  width: 'fit-content',
  margin: isCallPopup ? '0 0 5px 0' : '4px 0 5px 0',
  padding: '2px 4px',
  backgroundColor: getCallStatusColor(status, true),
  align: 'center',
  justify: 'center',
}))`
  border-radius: 5px;
`;

export const CallStatus = styled(PhoneDetails).attrs(({ status }) => ({
  marginTop: 0,
  color: getCallStatusColor(status, false),
  fontWeight: 'semibold',
  ellipsis: true,
  lineHeight: 1,
}))``;
export const AirAmbulanceStatus = styled(CallStatus).attrs(() => ({
  color: 'secondaryText',
  fontWeight: 'normal',
}))``;

export const TextMessage = styled(Text).attrs({
  color: 'secondaryText',
  size: 'small',
  ellipsis: true,
})``;

export const TimeBox = styled(Box).attrs({
  flex: '0 0 auto',
  marginLeft: '10px',
  overflow: 'hidden',
})``;

export const Time = styled(Text).attrs({
  color: 'secondaryText',
  size: 'xsmall',
  textAlign: 'right',
})``;

export const RemoveCall = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 10px 15px;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 2px;
  border-top: ${({ theme }) => `3px solid ${theme.colors.background}`};
  border-bottom: ${({ theme }) => `3px solid ${theme.colors.background}`};
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
  color: ${({ theme }) => theme.colors.primaryText};
  font-size: 14px;
  line-height: 1.2;
  text-align: center;
  transition: background-color 0.3s;
  user-select: none;
  &:hover {
    background-color: ${({ theme }) => theme.colors.highlights};
  }
`;
